import mixpanel from 'mixpanel-browser';

/**
 * Simple MixpanelService
 * - Respects dataCollectionPermission (if 1, no tracking)
 * - Auto-initializes on first use
 */
class MixpanelService {
  constructor() {
    this.mixpanel = null;
    this.isEnabled = false;
    this.initialize();
  }

  /**
   * Initialize Mixpanel
   */
  initialize() {
    // Only initialize in production
    if (import.meta.env.MODE !== 'production') {
      console.log('[Mixpanel] Disabled in development mode');
      return;
    }

    const token = import.meta.env.VITE_MIXPANEL_TOKEN;

    if (!token) {
      console.warn('Mixpanel token not found. Analytics disabled.');
      return;
    }

    try {
      mixpanel.init(token, {
        debug: false,
        track_pageview: true,
        persistence: 'localStorage',
        api_host: 'https://api-eu.mixpanel.com',
      });
      this.mixpanel = mixpanel;
      this.isEnabled = true;
    } catch (error) {
      console.error('Failed to initialize Mixpanel:', error);
    }
  }

  /**
   * Hash user ID for privacy (matches Node.js crypto.createHash)
   */
  async hashUserId(userId) {
    const userIdString = String(userId);
    // Removed debug logging of userId and hash for privacy

    const msgBuffer = new TextEncoder().encode(userIdString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    // Removed debug logging of generated hash for privacy
    return hashHex;
  }

  /**
   * Check if tracking is allowed for this therapist
   */
  canTrack(dataCollectionPermission) {
    // If dataCollectionPermission == 1, do NOT track
    return dataCollectionPermission !== 1;
  }

  /**
   * Track an event
   */
  async track(eventName, properties = {}, dataCollectionPermission = 0, userId = null) {
    if (!this.isEnabled || !this.canTrack(dataCollectionPermission)) {
      return;
    }

    try {
      // Identify user with hashed ID if provided
      if (userId) {
        const hashedUserId = await this.hashUserId(userId);
        this.mixpanel.identify(hashedUserId);
      }

      const eventData = {
        ...properties,
        timestamp: new Date().toISOString(),
        environment: import.meta.env.MODE,
      };

      this.mixpanel.track(eventName, eventData);
    } catch (error) {
      console.error('Failed to track event:', eventName, error);
    }
  }
}

// Export a singleton instance
export const mixpanelService = new MixpanelService();
